import * as api from '@apis/commission-rates';
import { CommissionRateTemplateCreateModal } from '@components/commission-rate/CommissionRateTemplateCreateModal';
import { UserListModal } from '@components/commission-rate/UserListModal';
import { useAuthentication } from '@hooks/authentication';
import { ChannelCommissionRate, CommissionRateTemplateExtend } from '@models/commission-rates';
import { useCommissionRateTemplatesStore } from '@stores/commissionRateTemplatesStore';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Input, Table } from 'antd';
import { filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

export const SalesChannelLabel: { [key: string]: string } = {
  app: '앱',
  'naver-store': '네이버',
  'kakao-reservation': '카카오',
  benepia: '베네피아',
};

export const CommissionRateTemplatesPage: React.FC = () => {
  const [editItem, setEditItem] = useState<CommissionRateTemplateExtend>();
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showUserListModal, setShowUserListModal] = useState<string>();
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const searchInputRef = useRef<any>();

  const store = useCommissionRateTemplatesStore();
  const dialog = useOkCancelDialog();

  useAuthentication();

  const commissionRateTemplatesQuery = useQuery({
    queryKey: [`commission-rate-templates`, JSON.stringify(store.searchQuery)],
    queryFn: () => api.searchCommissionRateTemplates(store.searchQuery),
  });

  const deleteCommissionRateTemplateMutation = useMutation({
    mutationFn: api.deleteCommissionRateTemplate,
    onSuccess: () => {
      commissionRateTemplatesQuery.refetch();
    },
  });
  const commissionRateTemplates = commissionRateTemplatesQuery.data?.data;

  useEffect(() => {
    if (commissionRateTemplates) {
      store.setCommissionRateTemplatesContainer(commissionRateTemplates);
    }
  }, [commissionRateTemplates]);

  const TABLE_COLUMNS: ColumnsType<CommissionRateTemplateExtend> = [
    {
      title: 'ID',
      dataIndex: '_id',
      render: (_id) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(_id);
                toast.dark('ID를 클립보드에 복사했습니다', {
                  position: 'top-center',
                  autoClose: 3000,
                  hideProgressBar: true,
                  closeOnClick: true,
                  pauseOnHover: true,
                  pauseOnFocusLoss: false,
                  draggable: true,
                  progress: undefined,
                  bodyStyle: {
                    color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                    textAlign: 'center',
                    fontFamily: 'Pretendard',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '20px',
                  },
                  theme: 'dark',
                });
              }}>
              ID
            </Button>
          </div>
        );
      },
    },
    {
      title: '이름',
      render: (record: CommissionRateTemplateExtend) => {
        return record.title;
      },
    },
    {
      title: '메모',
      render: (record: CommissionRateTemplateExtend) => {
        return record.memo;
      },
    },
    {
      title: '수수료율',
      render: (record: CommissionRateTemplateExtend) => {
        return (
          <div>
            {Object.keys(record.channelCommissionRate!)
              .map((item) => {
                return `${SalesChannelLabel[item]}: ${record.channelCommissionRate?.[
                  `${item as keyof ChannelCommissionRate}`
                ]!.ratePercent}%`;
              })
              .join('\n')}
          </div>
        );
      },
    },
    {
      title: '적용대상',
      align: 'center',
      width: 100,
      render: (record: CommissionRateTemplateExtend) => {
        return (
          <div>
            {record?.userCount || 0}명
            <Button
              onClick={() => {
                setShowUserListModal(record._id);
              }}>
              상세보기
            </Button>
          </div>
        );
      },
    },
    {
      title: '수정',
      align: 'center',
      width: 100,
      render: (record: CommissionRateTemplateExtend) => {
        return (
          <Button
            onClick={() => {
              setEditItem(record);
              setShowCreateModal(true);
            }}>
            수정
          </Button>
        );
      },
    },
    {
      title: '삭제',
      align: 'center',
      width: 100,
      render: (record: CommissionRateTemplateExtend) => {
        const isDefaultTemplate = record?.default;
        if (isDefaultTemplate) {
          return <div>기본 수수료</div>;
        }
        return (
          <Button
            onClick={() => {
              const applyUserCount = record.userCount || 0;
              if (applyUserCount) {
                dialog.open({
                  content: '적용중인 대상이 있어 삭제할 수 없습니다.',
                  type: 'ok',
                });
                return;
              }
              dialog.open({
                title: '삭제',
                content: '삭제하시겠습니까?',
                type: 'ok_cancel',
                onConfirm: () => {
                  deleteCommissionRateTemplateMutation.mutate(record._id!);
                },
              });
            }}>
            삭제
          </Button>
        );
      },
    },
    {
      title: '생성',
      dataIndex: 'createdAt',
      render: (createdAt) => {
        if (!createdAt) {
          return '-';
        }
        return moment(createdAt).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      title: '수정',
      dataIndex: 'updatedAt',
      render: (updatedAt) => {
        if (!updatedAt) {
          return '-';
        }
        return moment(updatedAt).format('YYYY.MM.DD HH:mm');
      },
    },
  ];

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      {showCreateModal && (
        <CommissionRateTemplateCreateModal
          data={editItem}
          open={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setEditItem(undefined);
          }}
          needRefetch={() => {
            commissionRateTemplatesQuery.refetch();
          }}></CommissionRateTemplateCreateModal>
      )}
      {!!showUserListModal && (
        <UserListModal
          commissionRateId={showUserListModal}
          open={!!showUserListModal}
          onClose={() => setShowUserListModal(undefined)}
        />
      )}
      <div className="page-header">
        <div>수수료율 템플릿 관리</div>
      </div>
      <div style={{ display: 'flex' }}>
        <div className="page-content" style={{ width: '100%', height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <div style={{ display: 'flex', gap: '12px', width: '100%', height: '52px', alignItems: 'center' }}>
            <Input
              className={filterActiveClassName(!!store.searchQuery.keyword)}
              ref={searchInputRef}
              style={{ width: '240px' }}
              value={searchKeyword}
              placeholder={'검색어 입력 후 엔터'}
              onChange={(e) => {
                setSearchKeyword(e.target.value);
              }}
              onKeyUp={(e) => {
                if (e.code === 'Enter' && searchKeyword) {
                  store.updateSearchQuery({ keyword: searchKeyword });
                }
              }}></Input>
            <Button
              onClick={() => {
                setSearchKeyword(undefined);
                store.resetSearchQuery();
              }}>
              검색 초기화
            </Button>
            <div style={{ fontWeight: 'bold' }}>검색결과: {store.searchResultContainer.total}</div>
            <div style={{ display: 'flex', marginLeft: 'auto', gap: '12px', alignItems: 'center' }}>
              <Button
                onClick={() => {
                  setShowCreateModal(true);
                }}>
                신규 템플릿 생성
              </Button>
            </div>
          </div>
          <Table
            columns={TABLE_COLUMNS}
            scroll={{ x: 'max-content', y: 'calc(100dvh - 280px)' }}
            style={{ whiteSpace: 'pre-wrap' }}
            rowKey={(record) => record._id!}
            dataSource={store.searchResultContainer.contents.filter((item) => item?._id)}
            pagination={{
              current: (store.searchResultContainer.page || 0) + 1,
              pageSize: store.searchResultContainer.size || 20,
              total: store.searchResultContainer.total,
            }}
            loading={commissionRateTemplatesQuery.isLoading}
            onChange={(e) => {
              store.updateSearchQuery({ page: (e.current || 2) - 1, size: e.pageSize || 20 });
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CommissionRateTemplatesPage;
